import React, { useMemo } from "react";
import { AxiosError } from "axios";
import { ApiError } from "fe-shared/src/types/common";
import { QueryKey, useInfiniteQuery } from "react-query";

import { useHistory, useRouteMatch } from "react-router-dom";

import TransfersDataGrid from "fe-shared-transactions/src/components/TransfersDataGrid";

import { getRequestParams } from "fe-shared-transactions/src/helpers/transfersConverter";
import { getTransfersFilterParams } from "fe-shared-transactions/src/helpers/queryString";

import { hasPermission } from "fe-shared/src/helpers/permissions";
import { PermissionLevel, Company } from "fe-shared/src/types/domain";
import { TRANSFERS_QUERY_KEY, PAGE_SIZE, PERMISSIONS } from "consts";
import {
  TransferResponse,
  TransferListItem,
} from "fe-shared-transactions/src/types";
import {
  DataGridFilters,
  DataGridOptions,
} from "fe-shared/src/components/DataGrid/modules/types";
import useStorage from "fe-shared/src/hooks/useStorage";
import useQueryFilters from "fe-shared-transactions/src/hooks/useQueryFilters";

import * as api from "fe-shared-transactions/src/components/TransfersDataGrid/modules/api";

interface Props {
  lastOpenedTransfersNumber: string | null;
  clearLastOpenedTransfersNumber: () => void;
  company: Company;
}

const Transfers = ({
  lastOpenedTransfersNumber,
  clearLastOpenedTransfersNumber,
  company: { companyId, permissions, displayName, businessRoleCode },
}: Props) => {
  const history = useHistory();
  const match = useRouteMatch();
  const [options, setOptions] = useStorage<DataGridOptions>(
    `portalTransfersOptions-${companyId}`,
    {
      sortDirection: "desc",
      sortField: "completedDate",
    },
    true
  );
  const [filters, setFilters] = useStorage<DataGridFilters>(
    `portalTransfersFilters-${companyId}`,
    {},
    true
  );

  const canExport = hasPermission(permissions, {
    name: PERMISSIONS.transfersListExport,
    level: PermissionLevel.Full,
  });

  const canViewTransfers = hasPermission(permissions, {
    name: PERMISSIONS.transfersList,
    level: PermissionLevel.Read,
  });

  const canViewDetails = hasPermission(permissions, {
    name: PERMISSIONS.transfersDetails,
    level: PermissionLevel.Read,
  });

  const canCreateInvoice = hasPermission(permissions, {
    name: PERMISSIONS.invoiceCreate,
    level: PermissionLevel.Full,
  });

  const requestParams = useMemo(() => getRequestParams(filters, options), [
    filters,
    options,
  ]);

  const transfersQueryKey: QueryKey = [
    TRANSFERS_QUERY_KEY,
    requestParams,
    companyId,
  ];

  const transfers = useInfiniteQuery<
    TransferResponse<TransferListItem>,
    AxiosError<ApiError>
  >(transfersQueryKey, api.getTransfers, {
    enabled: canViewTransfers,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    getFetchMore: (lastGroup, allGroups) =>
      allGroups.length * PAGE_SIZE < lastGroup.total
        ? allGroups.length + 1
        : undefined,
  });

  useQueryFilters(setFilters, getTransfersFilterParams);

  return canViewTransfers ? (
    <TransfersDataGrid
      filters={filters}
      options={options}
      companyId={companyId}
      setFilters={setFilters}
      setOptions={setOptions}
      displayName={displayName}
      canExport={canExport}
      canCreateInvoice={canCreateInvoice}
      transfers={transfers}
      transfersQueryKey={transfersQueryKey}
      businessRoleCode={businessRoleCode}
      lastOpenedTransfersNumber={lastOpenedTransfersNumber}
      clearLastOpenedTransfersNumber={clearLastOpenedTransfersNumber}
      onCellClicked={(e: any) => {
        const eventTag = "A";
        const currentNode = (e.event?.target as Element).nodeName;
        const isEventTagClicked = eventTag === currentNode;

        if (canViewDetails && !isEventTagClicked) {
          history.push(`${match.url}/${e.data.transferNumber}`);
        }
      }}
    />
  ) : null;
};

export default Transfers;
